import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

interface Application {
  id: string;
  name: string;
  description: string;
  url: string;
  icon: string;
  roles: string[];  // Which roles can see this app. Empty = everyone.
  external: boolean;
}

/**
 * Static application manifest for Release 1.
 * In future releases, this could come from a DynamoDB table or API.
 */
const APPLICATION_MANIFEST: Application[] = [
  {
    id: 'pricing-calculator',
    name: 'Sales & Pricing',
    description: 'Catalog, estimates, proposals, and contracts',
    url: '', // Will be populated when Pricing Calculator deploys
    icon: '💰',
    roles: ['owner', 'admin', 'designer'],
    external: false,
  },
  {
    id: 'proedge',
    name: 'Project Operations',
    description: 'Scheduling, phases, and field management',
    url: 'https://app.proedge.com',
    icon: '🏗️',
    roles: ['owner', 'admin', 'pm', 'superintendent'],
    external: true,
  },
  {
    id: 'gohighlevel',
    name: 'CRM',
    description: 'Leads, contacts, and sales pipeline',
    url: 'https://app.impdigital.co/',
    icon: '📊',
    roles: ['owner', 'admin', 'designer'],
    external: true,
  },
  {
    id: 'docusign',
    name: 'DocuSign',
    description: 'Contract signatures and document routing',
    url: 'https://app.docusign.com',
    icon: '✍️',
    roles: ['owner', 'admin', 'designer', 'pm'],
    external: true,
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    description: 'Team communication and collaboration',
    url: 'https://teams.microsoft.com',
    icon: '💬',
    roles: [],  // Everyone
    external: true,
  },
  {
    id: 'outlook',
    name: 'Outlook',
    description: 'Email and calendar',
    url: 'https://outlook.office.com',
    icon: '📧',
    roles: [],  // Everyone
    external: true,
  },
];

export function ApplicationLauncher() {
  const { user } = useAuth();
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    // Load pinned apps from localStorage for now (will move to UserPreferences table later)
    const stored = localStorage.getItem('unique-ai-pinned-apps');
    return stored ? JSON.parse(stored) : [];
  });

  const userGroups = user?.groups || [];

  // Filter apps by role
  const authorizedApps = APPLICATION_MANIFEST.filter((app) => {
    if (app.roles.length === 0) return true;  // Empty roles = everyone
    return app.roles.some((role) => userGroups.includes(role));
  });

  // Sort: pinned first, then alphabetical
  const sortedApps = [...authorizedApps].sort((a, b) => {
    const aPinned = pinnedIds.includes(a.id);
    const bPinned = pinnedIds.includes(b.id);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    return a.name.localeCompare(b.name);
  });

  function togglePin(appId: string) {
    const updated = pinnedIds.includes(appId)
      ? pinnedIds.filter((id) => id !== appId)
      : [...pinnedIds, appId];
    setPinnedIds(updated);
    localStorage.setItem('unique-ai-pinned-apps', JSON.stringify(updated));
  }

  function launchApp(app: Application) {
    if (!app.url) return;  // No URL yet (e.g., Pricing Calculator not deployed)
    window.open(app.url, app.external ? '_blank' : '_self');
  }

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">Applications</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sortedApps.map((app) => (
          <div
            key={app.id}
            className="relative bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all group"
          >
            {/* Pin button */}
            <button
              onClick={(e) => { e.stopPropagation(); togglePin(app.id); }}
              className={`absolute top-2 right-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity ${
                pinnedIds.includes(app.id) ? 'opacity-100 text-blue-500' : 'text-gray-400'
              }`}
              title={pinnedIds.includes(app.id) ? 'Unpin' : 'Pin to top'}
            >
              {pinnedIds.includes(app.id) ? '📌' : '📍'}
            </button>

            {/* App content - clickable */}
            <button
              onClick={() => launchApp(app)}
              disabled={!app.url}
              className="w-full text-left"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">{app.icon}</span>
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 text-sm flex items-center gap-1">
                    {app.name}
                    {app.external && (
                      <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{app.description}</p>
                  {!app.url && (
                    <p className="text-xs text-amber-500 mt-1">Coming soon</p>
                  )}
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
